<script setup>
import {ref, computed, onMounted, onUnmounted} from 'vue';
import {Carousel, Slide} from 'vue3-carousel';
import 'vue3-carousel/dist/carousel.css';
import useBanners from '@/composables/useBanners.js';
import {resolveBannerImageForViewport} from '@/composables/banners/bannerViewportSizes.js';
import {isOnLocalDomain} from '@/composables/useLocalDomains';

const {data: banners} = useBanners();

const viewportWidth = ref(window.innerWidth);

function onResize() {
    viewportWidth.value = window.innerWidth;
}

onMounted(() => window.addEventListener('resize', onResize));
onUnmounted(() => window.removeEventListener('resize', onResize));

const visibleBanners = computed(() => {
    if (!isOnLocalDomain.value || !Array.isArray(banners.value)) {
        return [];
    }

    return banners.value
        .map((banner) => ({
            ...banner,
            image: resolveBannerImageForViewport(
                banner.images ?? null,
                viewportWidth.value,
                banner.fallback_images ?? true,
            ),
        }))
        .filter((banner) => banner.image);
});

const isMultiple = computed(() => visibleBanners.value.length > 1);

const carousel = ref(null);

function previous() {
    if (carousel.value) {
        carousel.value.prev();
    }
}

function next() {
    if (carousel.value) {
        carousel.value.next();
    }
}
</script>

<template>
  <div
    v-if="visibleBanners.length"
    class="tailwind"
  >
    <div class="tw-group tw-relative tw-w-full tw-max-w-default-container tw-mx-auto tw-mb-6">
      <Carousel
        ref="carousel"
        :items-to-show="1"
        :items-to-scroll="1"
        :wrap-around="isMultiple"
        :autoplay="isMultiple ? 5000 : 0"
        :pause-autoplay-on-hover="true"
      >
        <Slide
          v-for="banner in visibleBanners"
          :key="banner.id"
          class="tw-w-full tw-min-w-0"
        >
          <component
            :is="banner.cta_url ? 'a' : 'div'"
            :href="banner.cta_url ?? undefined"
            :target="banner.cta_url ? '_blank' : undefined"
            :rel="banner.cta_url ? 'noopener noreferrer' : undefined"
            class="tw-block tw-w-full tw-min-w-0"
            :class="{'tw-cursor-pointer': banner.cta_url}"
          >
            <img
              :src="banner.image"
              :alt="banner.title"
              class="tw-block tw-w-full tw-h-auto tw-rounded-xl"
            >
          </component>
        </Slide>

        <template
          v-if="isMultiple"
          #addons
        >
          <svg-vue
            icon="icons/rounded-arrow-left"
            class="tw-w-8 tw-h-8 tw-absolute tw-top-1/2 -tw-translate-y-1/2 tw-left-3 tw-cursor-pointer tw-opacity-0 tw-transition-opacity group-hover:tw-opacity-100"
            @click="previous"
          />

          <svg-vue
            icon="icons/rounded-arrow-right"
            class="tw-w-8 tw-h-8 tw-absolute tw-top-1/2 -tw-translate-y-1/2 tw-right-3 tw-cursor-pointer tw-opacity-0 tw-transition-opacity group-hover:tw-opacity-100"
            @click="next"
          />
        </template>
      </Carousel>
    </div>
  </div>
</template>
